import Emoji from "@tiptap/extension-emoji";
import type {EmojiItem, EmojiOptions, EmojiStorage} from "@tiptap/extension-emoji";
import {ReactRenderer} from "@tiptap/react";
import type {SuggestionKeyDownProps, SuggestionProps} from "@tiptap/suggestion";
import type {MarkdownNodeSpec} from "tiptap-markdown";
import {mountLayer} from "../../../newVariablePicker/editor/variablePickerPlugins";
import EmojiSuggestionList, {
    type EmojiSuggestionListHandle,
    type EmojiSuggestionListProps,
} from "./EmojiSuggestionList";
import {EMOJIS, findEmojis} from "./emojiCatalog";

type EmojiExtensionStorage = EmojiStorage & {
    markdown: MarkdownNodeSpec<EmojiOptions>;
};

const renderSuggestions = () => {
    let renderer: ReactRenderer<EmojiSuggestionListHandle, EmojiSuggestionListProps> | null = null;
    let unmount: (() => void) | null = null;

    const listProps = (props: SuggestionProps<EmojiItem>): EmojiSuggestionListProps => ({
        items: props.items,
        query: props.query,
        onSelect: props.command,
    });

    return {
        onStart: (props: SuggestionProps<EmojiItem>) => {
            renderer = new ReactRenderer(EmojiSuggestionList, {editor: props.editor, props: listProps(props)});
            // Above the dialog the field sits in, like the variable popup.
            renderer.element.style.zIndex = mountLayer(props.editor.view.dom);
            unmount = props.mount(renderer.element);
        },
        onUpdate: (props: SuggestionProps<EmojiItem>) => {
            renderer?.updateProps(listProps(props));
        },
        onKeyDown: ({event}: SuggestionKeyDownProps) => {
            const handled = renderer?.ref?.onKeyDown(event) ?? false;
            // Escape closes the list, never the dialog around the field.
            if (handled || event.key === 'Escape' || event.key === 'Esc') event.stopPropagation();
            return handled;
        },
        onExit: () => {
            unmount?.();
            renderer?.destroy();
            unmount = null;
            renderer = null;
        },
    };
};

/** Emoji after typing `:`, stored as the emoji character itself, not as a `:shortcode:`. */
const EmojiExtension = Emoji.extend<EmojiOptions, EmojiExtensionStorage>({
    addOptions() {
        const parent = this.parent?.() as EmojiOptions;

        return {
            ...parent,
            emojis: EMOJIS,
            suggestion: {
                ...parent.suggestion,
                items: ({query}) => findEmojis(EMOJIS, query),
                render: renderSuggestions,
            },
        };
    },

    addStorage() {
        const markdown: MarkdownNodeSpec<EmojiOptions> = {
            serialize(state, node) {
                const item = this.options.emojis.find(candidate => candidate.name === node.attrs.name);
                state.text(item?.emoji ?? `:${node.attrs.name}:`);
            },
        };

        return {
            ...(this.parent?.() as EmojiStorage),
            markdown,
        };
    },
});

export default EmojiExtension;
